import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { SUPPORTED_LANG_CODES, type SupportedLangCode } from '@/application/i18n/language'
import { useSaveManagedTerm } from '@/application/services/taxonomy.service'
import { handleApiError } from '@/infrastructure/api/client'
import { termLabelIn, type ManagedTaxonomyTerm } from '@/domains/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/ui/components/common/Dialog'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/forms/Input'

const LABEL_MAX_LENGTH = 100
/** Same emoji flags as the language selectors of the headers. */
const LOCALE_FLAGS: Record<SupportedLangCode, string> = { fr: '🇫🇷', en: '🇬🇧', es: '🇪🇸' }

/** A new category is written in French, which makes French its source locale. */
const NEW_TERM_LOCALE: SupportedLangCode = 'fr'

type CategoryFormData = Record<SupportedLangCode, string>

const makeCategorySchema = (
  translate: (key: string, opts?: Record<string, unknown>) => string,
  requiredLocale: SupportedLangCode,
) => {
  const label = z
    .string()
    .trim()
    .max(LABEL_MAX_LENGTH, translate('validation.max_chars', { count: LABEL_MAX_LENGTH }))
  return z
    .object({ fr: label, en: label, es: label })
    .refine((data) => data[requiredLocale].length > 0, {
      path: [requiredLocale],
      message: translate('validation.required'),
    })
}

interface CategoryDialogProps {
  /** The category to edit, or `null` to create one. */
  term: ManagedTaxonomyTerm | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Create / edit dialog of a category: one label field per language. The label
 * in the category's own source language is its canonical name and is required;
 * the others are optional (blank means "no translation"). Mount it with a
 * `key` per category so the form starts from that category's labels.
 */
export function CategoryDialog({ term, open, onOpenChange }: CategoryDialogProps) {
  const { t } = useTranslation()
  const { mutateAsync: saveTerm } = useSaveManagedTerm()
  const requiredLocale = term?.sourceLocale ?? NEW_TERM_LOCALE
  const schema = useMemo(() => makeCategorySchema(t, requiredLocale), [t, requiredLocale])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fr: term ? termLabelIn(term, 'fr') : '',
      en: term ? termLabelIn(term, 'en') : '',
      es: term ? termLabelIn(term, 'es') : '',
    },
  })

  const onSubmit = async (data: CategoryFormData) => {
    try {
      await saveTerm({ termId: term?.id, labels: data })
    } catch (error) {
      toast.error(handleApiError(error))
      return
    }
    onOpenChange(false)
    toast.success(t(term ? 'categories.update_success' : 'categories.create_success'))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) reset()
      }}
    >
      <DialogContent size="wide">
        <DialogHeader>
          <DialogTitle className="font-serif">
            {t(term ? 'categories.edit_title' : 'categories.add_title')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
          {SUPPORTED_LANG_CODES.map((locale) => (
            <div key={locale} className="space-y-1.5">
              <label htmlFor={`category-label-${locale}`} className="text-sm font-medium">
                <span aria-hidden="true" className="mr-2">
                  {LOCALE_FLAGS[locale]}
                </span>
                {t(`categories.label_${locale}`)}
                {locale === requiredLocale && <span className="text-destructive ml-1">*</span>}
              </label>
              <Input
                id={`category-label-${locale}`}
                {...register(locale)}
                placeholder={t(`categories.label_${locale}_placeholder`)}
              />
              {errors[locale] && (
                <p className="text-destructive text-xs">{errors[locale].message}</p>
              )}
            </div>
          ))}
          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" isLoading={isSubmitting}>
              {t(term ? 'categories.save' : 'categories.add_submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
